import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { StorageKeys } from '@core-interfaces/global';
import type { IAuthService } from '@core-services/auth';
import type { IActiveUserSession } from '@core-interfaces/global/session';
import type { ApiSuccessResponse } from '@core-interfaces/global/http';
import type { ILoginInput } from '@core-ports/inputs/login';
import type { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Microservice } from '@core-interfaces/global/microservice';
@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService {
  constructor(
    private http: HttpClient,
  ) {}

  isAuth(): boolean {
    const sessionToken = sessionStorage.getItem(StorageKeys.session_token);
    return sessionToken && sessionToken.length > 0 ? true : false;
  }

  UserSessionData(): IActiveUserSession | null {
    const sessionToken = sessionStorage.getItem(StorageKeys.session_token);
    if (!sessionToken) {
      return null;
    }
    return jwtDecode<IActiveUserSession>(sessionToken);
  }

  setSession(token: string, refreshToken?: string): void {
    sessionStorage.setItem(StorageKeys.session_token, token);
    if (refreshToken) {
      sessionStorage.setItem(StorageKeys.refresh_token, refreshToken);
    }
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(StorageKeys.refresh_token);
  }

  cleanSession(): void {
    sessionStorage.removeItem(StorageKeys.session_token);
    sessionStorage.removeItem(StorageKeys.refresh_token);
  }

  login(input: ILoginInput): Observable<ApiSuccessResponse<{
    token: string;
    refresh_token: string;
  }>> {
    return this.http.post<ApiSuccessResponse<{
      token: string;
      refresh_token: string;
    }>>(`/${Microservice.users_service}/users/auth/admin/login`, input);
  }

  refreshTokens(refreshToken: string): Observable<ApiSuccessResponse<{
    token: string;
    refresh_token: string;
  }>> {
    return this.http.post<ApiSuccessResponse<{
      token: string;
      refresh_token: string;
    }>>(`/${Microservice.users_service}/users/auth/refresh`, { refresh_token: refreshToken }, {
      headers: { 'x-skip-auth': 'true' }
    });
  }

  sendAdminRecoverMail(email: string): Observable<ApiSuccessResponse<boolean>> {
    return this.http.post<ApiSuccessResponse<boolean>>(
      `/${Microservice.users_service}/users/auth/admin/send-recover-mail`,
      { email },
      { headers: { 'x-skip-auth': 'true' } }
    );
  }

  validateRecoverCode(code: string): Observable<ApiSuccessResponse<{ recover_token: string }>> {
    return this.http.post<ApiSuccessResponse<{ recover_token: string }>>(
      `/${Microservice.users_service}/users/auth/validate-recover-code`,
      { code },
      { headers: { 'x-skip-auth': 'true' } }
    );
  }

  changePasswordWithRecoverToken(recover_token: string, new_password: string): Observable<ApiSuccessResponse<boolean>> {
    return this.http.post<ApiSuccessResponse<boolean>>(
      `/${Microservice.users_service}/users/auth/change-password-with-recover-token`,
      { recover_token, new_password },
      { headers: { 'x-skip-auth': 'true' } }
    );
  }

  changeMyPassword(current_password: string, new_password: string): Observable<ApiSuccessResponse<boolean>> {
    return this.http.post<ApiSuccessResponse<boolean>>(
      `/${Microservice.backoffice_service}/users/me/change-password`,
      { current_password, new_password }
    );
  }
}
